import { ExternalApiKey } from "@dashboard-v3/api";
import {
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { Trans, useTranslation } from "react-i18next";
import useSWR from "swr";
import request, { fetcher } from "utils/apiRequestWithErrorCode";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useState } from "react";
import { useSnackbar } from "notistack";
import ButtonWithLoading from "components/ButtonWithLoading";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import { red } from "@material-ui/core/colors";
import useClipboard from "utils/useClipboard";
import Confirm from "components/Confirm";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

export default function MxHeroApi() {
  const { t } = useTranslation("mxheroApi");
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const copyToClipboard = useClipboard();

  const [status, setStatus] = useState<
    "TOGGLING" | "DELETING" | "CREATING" | null
  >();

  const { data, isLoading, mutate } = useSWR<ExternalApiKey>(
    "/external-api-key",
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  async function toggleKeyState() {
    setStatus("TOGGLING");
    try {
      const newState = await request<ExternalApiKey>(
        "PATCH",
        "/external-api-key",
        {
          enable: !data.enabled,
        }
      );

      mutate(newState, { revalidate: false });

      const message = data.enabled ? "keyWasDisabled" : "keyWasEnabled";
      enqueueSnackbar(t(message), { variant: "success" });
    } catch (error) {
      const message = data.enabled ? "errorOnDisable" : "errorOnEnable";
      enqueueSnackbar(t(message), { variant: "error" });
    } finally {
      setStatus(null);
    }
  }

  async function deleteKey() {
    setStatus("DELETING");
    try {
      await request<ExternalApiKey>("DELETE", "/external-api-key");
      mutate(null);

      enqueueSnackbar(t("keyDeleted"), { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("errorOnDelete"), { variant: "error" });
    } finally {
      setStatus(null);
    }
  }

  async function createKey() {
    setStatus("CREATING");
    try {
      const newApiKey = await request<ExternalApiKey>(
        "POST",
        "/external-api-key",
        {}
      );
      mutate(newApiKey, { revalidate: false });

      enqueueSnackbar(t("createdKey"), { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("errorOnCreate"), { variant: "error" });
    } finally {
      setStatus(null);
    }
  }

  return (
    <Paper className={classes.container}>
      <Box display="flex" flexDirection="column">
        <Typography>{t("mainTitle")}</Typography>
        <Box display="flex" alignItems="center" gridGap=".5rem">
          <Typography variant="body2" color="textSecondary">
            <Trans t={t} i18nKey="subTitleWithSmime">
              Manage your <SubtitleLink to="datasets" /> and{" "}
              <SubtitleLink to="smime" /> through an API.
            </Trans>
          </Typography>
          {data && <Help orgId={data.organizationId} />}
        </Box>
      </Box>
      {isLoading && !status && (
        <Box mt={4}>
          <LinearProgress />
        </Box>
      )}
      {data?.value && (
        <>
          <Box mt={3} display="flex" gridGap="1rem" alignItems="center">
            <TextField
              name="openAIToken"
              label={t("openAIToken.apiKey.label")}
              variant="outlined"
              fullWidth
              className={classes.apiInput}
              value={data.value}
              InputProps={{
                disabled: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={t("common:copyToClipboard")}>
                      <IconButton
                        onClick={() => copyToClipboard(data.value)}
                        edge="end"
                      >
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            {!data.enabled && (
              <ButtonWithLoading
                onClick={toggleKeyState}
                loading={status === "TOGGLING"}
              >
                {t("enableApiKey")}
              </ButtonWithLoading>
            )}

            {data.enabled && (
              <Tooltip title={t("disableApiKeyTooltip")}>
                <Confirm
                  message={t("disableApiKeyConfirmation")}
                  onAccept={toggleKeyState}
                  actionable={openConfirm => (
                    <ButtonWithLoading
                      loading={status === "TOGGLING"}
                      onClick={openConfirm}
                    >
                      {t("disableApiKey")}
                    </ButtonWithLoading>
                  )}
                />
              </Tooltip>
            )}

            <Confirm
              message={t("deleteApiKeyConfirmation")}
              onAccept={deleteKey}
              actionable={openConfirm => (
                <ButtonWithLoading
                  color="secondary"
                  onClick={openConfirm}
                  loading={status === "DELETING"}
                >
                  {t("common:delete")}
                </ButtonWithLoading>
              )}
            />
          </Box>
          {data?.value && !data.enabled && (
            <Box display="flex" gridGap=".5rem" mt={2} alignItems="center">
              <Box display="flex" color={red[200]} alignItems="stretch">
                <RemoveCircleIcon />
              </Box>
              <Typography variant="body2">{t("isDisabled")}</Typography>
            </Box>
          )}
        </>
      )}

      {data && !data?.value && (
        <Box mt={3}>
          <ButtonWithLoading
            loading={status === "CREATING"}
            variant="contained"
            disableElevation
            onClick={createKey}
          >
            {t("create")}
          </ButtonWithLoading>
        </Box>
      )}
    </Paper>
  );
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    marginTop: "20px",
    padding: "16px",
  },
  apiInput: { maxWidth: "50%", display: "block" },
});

function SubtitleLink(props: { to: "datasets" | "smime" }) {
  const { to } = props;
  const { t } = useTranslation(["sidebar", to]);
  const linkText = to === "smime" ? "certificates" : to;
  const paths = { datasets: "/datasets", smime: "/smime-certs" };

  return (
    <Tooltip title={t(`${to}:description`)} aria-label={t(`${to}:description`)}>
      <Link href={paths[to]} target="_blank">
        {t(linkText)}
      </Link>
    </Tooltip>
  );
}

function Help({ orgId }: { orgId: string }) {
  const { t } = useTranslation("mxheroApi");
  const [anchorEl, setAnchorEl] = useState(null);
  const copyToClipboard = useClipboard();

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <HelpOutlineIcon
        fontSize="small"
        color="primary"
        style={{ cursor: "pointer" }}
        onClick={handleClick}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box p={2}>
          <span style={{ fontWeight: 500 }}>{t("help.apiDoc")}</span>
          <Box display="flex" flexDirection="column" gridGap=".5rem" mt={1}>
            <Link
              href="https://mxhero.helpjuice.com/en_US/general-information/mxhero-v3-api-smime"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("help.smime")}
            </Link>
            <Link
              href="https://mxhero.helpjuice.com/en_US/general-information/mxhero-v3-api-dataset"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("help.datasets")}
            </Link>
          </Box>
          <Box mt={3}>{t("help.yourOrgId")}</Box>

          <Link
            onClick={() => copyToClipboard(orgId)}
            style={{ cursor: "pointer" }}
          >
            <Box display="flex" alignItems="center" gridGap=".5rem" mt={1}>
              {orgId}
              <FileCopyOutlinedIcon fontSize="small" />
            </Box>
          </Link>
        </Box>
      </Popover>
    </div>
  );
}
